import React from "react";
import {
  listSystemPermissions,
  getRoleSystemPermissions,
  replaceRoleSystemPermissions,
  RolePermissionAssignment,
  SystemPermissionDto,
} from "../../services/systemPermissions.service.ts";
import { roleApiError } from "../../services/role.service.js";
import { notification } from "../../utils/Notifications.tsx";
import Button from "../../components/Button.tsx";

const SCOPES = ["global", "branch", "own", "assigned"] as const;
const ENFORCED_MODULES = new Set(["users", "roles", "partners", "files"]);

type Draft = Record<string, { enabled: boolean; scope: string }>;

const RolePermissionEditor = ({
  roleId,
  canEdit,
}: {
  roleId: number;
  canEdit: boolean;
}) => {
  const [catalog, setCatalog] = React.useState<SystemPermissionDto[]>([]);
  const [draft, setDraft] = React.useState<Draft>({});
  const [baseline, setBaseline] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [structural, setStructural] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [permissions, matrix] = await Promise.all([
        listSystemPermissions(),
        getRoleSystemPermissions(roleId),
      ]);
      setCatalog(permissions);
      setStructural(matrix.structural === true);
      const next: Draft = {};
      permissions.forEach((item) => {
        next[item.code] = { enabled: false, scope: "global" };
      });
      (matrix.assignments || []).forEach((row: RolePermissionAssignment) => {
        next[row.code] = { enabled: true, scope: row.scope || "global" };
      });
      setDraft(next);
      setBaseline(JSON.stringify(next));
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const dirty = JSON.stringify(draft) !== baseline;
  const grouped = catalog.reduce<Record<string, SystemPermissionDto[]>>(
    (acc, item) => {
      acc[item.module] = acc[item.module] || [];
      acc[item.module].push(item);
      return acc;
    },
    {},
  );

  const save = async () => {
    const permissions = Object.entries(draft)
      .filter(([, value]) => value.enabled)
      .map(([code, value]) => ({
        code,
        scope: value.scope as RolePermissionAssignment["scope"],
      }));
    setSaving(true);
    try {
      const matrix = await replaceRoleSystemPermissions(roleId, permissions);
      const next: Draft = {};
      catalog.forEach((item) => {
        next[item.code] = { enabled: false, scope: "global" };
      });
      (matrix.assignments || []).forEach((row) => {
        next[row.code] = { enabled: true, scope: row.scope || "global" };
      });
      setDraft(next);
      setBaseline(JSON.stringify(next));
      notification("Éxito", "Permisos actualizados", "success");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-secondary">Cargando permisos…</p>;
  }

  if (structural) {
    return (
      <div className="alert alert-info">
        <strong>Acceso total.</strong> Este es un rol estructural
        (grantsAllPermissions). Su autoridad no depende de la matriz de
        permisos y la configuración es de solo lectura.
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert-warning">
        Algunos módulos continúan en migración RBAC. Asignar un permiso de
        catálogo no implica que el módulo esté enforced. Scopes branch/own/assigned
        solo tienen efecto cuando el endpoint AUTHORIZED carga un resource.
      </div>
      {Object.keys(grouped)
        .sort()
        .map((moduleName) => (
          <div key={moduleName} className="mb-3">
            <h6 className="text-uppercase fs-12px">
              {moduleName}
              {!ENFORCED_MODULES.has(moduleName) && (
                <span className="badge bg-light text-dark ms-2">Catálogo</span>
              )}
              {ENFORCED_MODULES.has(moduleName) && (
                <span className="badge bg-secondary ms-2">Enforced / parcial</span>
              )}
            </h6>
            {grouped[moduleName].map((item) => {
              const row = draft[item.code] || { enabled: false, scope: "global" };
              return (
                <div
                  key={item.code}
                  className="d-flex flex-wrap align-items-center gap-2 mb-2"
                >
                  <label className="form-check mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={!canEdit}
                      checked={row.enabled}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          [item.code]: {
                            ...row,
                            enabled: event.target.checked,
                          },
                        }))
                      }
                    />
                    <span className="form-check-label ms-1">
                      <code>{item.code}</code> — {item.description}
                    </span>
                  </label>
                  {row.enabled && (
                    <select
                      className="form-select form-select-sm w-auto"
                      disabled={!canEdit}
                      value={row.scope}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          [item.code]: { ...row, scope: event.target.value },
                        }))
                      }
                    >
                      {SCOPES.map((scope) => (
                        <option key={scope} value={scope}>
                          {scope}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      {canEdit && (
        <div className="d-flex justify-content-end">
          <div className="col-md-3">
            <Button
              type="button"
              variant="azul"
              title={saving ? "Guardando…" : dirty ? "Guardar cambios" : "Sin cambios"}
              disabled={saving || !dirty}
              onClick={save}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionEditor;
